import React, { useState } from 'react';
import { ChevronLeft, Calendar, Dumbbell, ChevronRight, Bug, History as HistoryIcon } from 'lucide-react';
import { Chip } from '../components/Chip';
interface TrainingHistoryProps {
  onBack: () => void;
  onViewAnalytics: (exercise: string) => void;
  onViewWorkout?: (workoutId: number) => void;
}
export function TrainingHistory({
  onBack,
  onViewAnalytics,
  onViewWorkout
}: TrainingHistoryProps) {
  const [filter, setFilter] = useState('All');
  const [showDebug, setShowDebug] = useState(false);
  const [emptyState, setEmptyState] = useState<'none' | 'no-workouts' | 'no-results'>('none');
  const workouts = [{
    id: 1,
    date: 'Oct 14, 2023',
    title: 'Full Body Power',
    exercises: 6,
    sets: 18,
    volume: '12,460 kg'
  }, {
    id: 2,
    date: 'Oct 12, 2023',
    title: 'Upper Body Focus',
    exercises: 5,
    sets: 15,
    volume: '8,920 kg'
  }, {
    id: 3,
    date: 'Oct 10, 2023',
    title: 'Lower Body Strength',
    exercises: 4,
    sets: 16,
    volume: '14,200 kg'
  }, {
    id: 4,
    date: 'Oct 7, 2023',
    title: 'Full Body Power',
    exercises: 6,
    sets: 18,
    volume: '11,850 kg'
  }];
  const displayedWorkouts = emptyState === 'none' ? workouts : [];
  const showEmptyState = emptyState !== 'none';
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-1183">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-1184">
        <Bug size={14} data-id="element-1185" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150" data-id="element-1186">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-1187">
            Debug Panel
          </p>
          <button onClick={() => {
        setEmptyState('none');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1188">
            ▶ Show workout history
          </button>
          <button onClick={() => {
        setEmptyState('no-workouts');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1189">
            ▶ Empty — no completed workouts
          </button>
          <button onClick={() => {
        setEmptyState('no-results');
        setFilter('This Month');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-1190">
            ▶ Empty — no results for filter
          </button>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-1191">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-1192">
          <ChevronLeft size={28} data-id="element-1193" />
        </button>
        <h1 className="text-xl font-extrabold text-slate-800" data-id="element-1194">History</h1>
        <div className="w-10" data-id="element-1195" /> {/* Spacer */}
      </div>

      {/* Filters */}
      <div className="px-6 pb-4 flex gap-2 overflow-x-auto shrink-0 no-scrollbar" data-id="element-1196">
        {['All', 'This Month', 'Last Month'].map(f => <Chip key={f} label={f} selected={filter === f} onClick={() => setFilter(f)} className="whitespace-nowrap" data-id="element-1197" />)}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-12" data-id="element-1198">
        {showEmptyState ? <div className="flex flex-col items-center justify-center pt-20 px-4 animate-in fade-in duration-300" data-id="element-1199">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-1200">
              <HistoryIcon size={28} className="text-slate-400" data-id="element-1201" />
            </div>

            <h2 className="text-lg font-extrabold text-slate-800 mb-2 text-center" data-id="element-1202">
              {emptyState === 'no-workouts' ? 'Complete your first workout to see it here' : 'No workouts found for this period'}
            </h2>

            <p className="text-sm font-medium text-slate-500 text-center max-w-xs leading-relaxed" data-id="element-1203">
              {emptyState === 'no-workouts' ? 'Your completed workouts will appear here once you finish a session.' : 'Try another filter or complete a new workout to see it here.'}
            </p>
          </div> : <div className="space-y-4" data-id="element-1204">
            {displayedWorkouts.map(workout => <button key={workout.id} onClick={() => onViewWorkout?.(workout.id)} className="w-full bg-white rounded-3xl p-5 shadow-sm border border-slate-100 hover:border-slate-200 transition-colors text-left" data-id="element-1205">
                <div className="flex items-center justify-between mb-3" data-id="element-1206">
                  <div className="flex items-center gap-2 text-slate-500" data-id="element-1207">
                    <Calendar size={14} data-id="element-1208" />
                    <span className="text-xs font-bold uppercase tracking-wider" data-id="element-1209">
                      {workout.date}
                    </span>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" data-id="element-1210" />
                </div>

                <h3 className="text-lg font-extrabold text-slate-800 mb-3" data-id="element-1211">
                  {workout.title}
                </h3>

                <div className="flex items-center gap-3" data-id="element-1212">
                  <div className="text-xs font-medium text-slate-600 bg-stone-50 px-3 py-1.5 rounded-lg" data-id="element-1213">
                    <span className="font-bold text-slate-800" data-id="element-1214">
                      {workout.exercises}
                    </span>{' '}
                    exercises
                  </div>
                  <div className="text-xs font-medium text-slate-600 bg-stone-50 px-3 py-1.5 rounded-lg" data-id="element-1215">
                    <span className="font-bold text-slate-800" data-id="element-1216">
                      {workout.sets}
                    </span>{' '}
                    sets
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-stone-50 px-3 py-1.5 rounded-lg" data-id="element-1217">
                    <Dumbbell size={14} className="text-emerald-500" data-id="element-1218" />
                    <span className="font-bold text-slate-800" data-id="element-1219">
                      {workout.volume}
                    </span>
                  </div>
                </div>
              </button>)}
          </div>}
      </div>
    </div>;
}